/**
 * Drives the Footballer Quiz screen: loads the day's attempt, runs the search-as-you-type autocomplete,
 * submits a guess (a player id), and accumulates the compared rows until the mystery is named (win) or the
 * guesses run out (lose). The server is the source of truth for the tile comparison, coins, and streak; the
 * mystery's identity only arrives in the response once the attempt is over.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient, extractErrorMessage } from '../../../helpers/apiClient.helper';
import { useEconomyStore } from '../../shared/stores/economyStore';
import { useInvalidateXpSummary } from '../../economy/hooks/useXpSummary';
import { useAnalytics } from '../../analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../analytics/events';
import {
  startFootballerRun,
  searchFootballers,
  guessFootballer,
  type FootballerGuessRow,
  type FootballerSuggestion,
  type FootballerReveal,
} from '../helpers/footballerQuizApi';
import { FOOTBALLER_GAME_KEY, FOOTBALLER_SEARCH_MIN_CHARS } from '../constants/footballerQuiz.constants';

type Phase = 'loading' | 'error' | 'playing' | 'over';

const TOAST_MS = 2200;
const SEARCH_DEBOUNCE_MS = 180;

export interface FootballerResult {
  won: boolean;
  reveal: FootballerReveal;
  guessesUsed: number;
  streak: number;
  coinsAwarded: number;
  share: string;
}

export function useFootballerQuizGame() {
  const apiClient = useApiClient();
  const syncCoins = useEconomyStore((s) => s.syncCoins);
  const invalidateXp = useInvalidateXpSummary();
  const { track } = useAnalytics();

  const [phase, setPhase] = useState<Phase>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [maxGuesses, setMaxGuesses] = useState(8);
  const [guesses, setGuesses] = useState<FootballerGuessRow[]>([]);
  const [guessesLeft, setGuessesLeft] = useState(8);
  const [streak, setStreak] = useState(0);
  const [result, setResult] = useState<FootballerResult | null>(null);
  const [overlayOpen, setOverlayOpen] = useState(false);

  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<FootballerSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selectedId = useRef<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    if (searchTimer.current) clearTimeout(searchTimer.current);
  }, []);

  const flashToast = useCallback((message: string) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  const guessedNames = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    setPhase('loading');
    setResult(null);
    setOverlayOpen(false);
    try {
      const run = await startFootballerRun(apiClient);
      setMaxGuesses(run.maxGuesses);
      setGuesses(run.guesses);
      setGuessesLeft(run.guessesLeft);
      setStreak(run.streak);
      guessedNames.current = new Set(run.guesses.map((g) => g.playerId));
      if (run.status !== 'IN_PROGRESS' && run.reveal && run.share != null) {
        setResult({
          won: run.status === 'WON',
          reveal: run.reveal,
          guessesUsed: run.guessesUsed,
          streak: run.streak,
          coinsAwarded: 0,
          share: run.share,
        });
        setOverlayOpen(true);
        setPhase('over');
        return;
      }
      setPhase('playing');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err, "Couldn't load today's game"));
      setPhase('error');
    }
  }, [apiClient]);

  useEffect(() => {
    track(ANALYTICS_EVENTS.GAME_STARTED, { game_key: FOOTBALLER_GAME_KEY });
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = useCallback(async (term: string) => {
    try {
      const players = await searchFootballers(apiClient, term);
      // Hide players already guessed today.
      const fresh = players.filter((p) => !guessedNames.current.has(p.id));
      setSuggestions(fresh);
      setShowDropdown(fresh.length > 0);
    } catch {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [apiClient]);

  const onInputChange = useCallback((text: string) => {
    setInput(text);
    selectedId.current = null;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (text.trim().length < FOOTBALLER_SEARCH_MIN_CHARS) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }
    searchTimer.current = setTimeout(() => void runSearch(text.trim()), SEARCH_DEBOUNCE_MS);
  }, [runSearch]);

  const submit = useCallback(async (playerId: string, label: string) => {
    if (busy || phase !== 'playing') return;
    setBusy(true);
    setShowDropdown(false);
    try {
      const res = await guessFootballer(apiClient, playerId);
      const row: FootballerGuessRow = { playerId, name: res.guess.name, nat: res.guess.nat, league: res.guess.league, team: res.guess.team, pos: res.guess.pos, age: res.guess.age };
      setGuesses((prev) => [...prev, row]);
      setGuessesLeft(res.guessesLeft);
      guessedNames.current.add(playerId);
      setInput('');
      setSuggestions([]);
      selectedId.current = null;

      if (res.done) {
        if (typeof res.coins === 'number') syncCoins(res.coins);
        invalidateXp(); // completion earns XP even when coins are daily-capped
        setStreak(res.streak);
        setResult({
          won: res.won,
          reveal: res.reveal,
          guessesUsed: res.guessesUsed,
          streak: res.streak,
          coinsAwarded: res.coinsAwarded,
          share: res.share,
        });
        setPhase('over');
        // Let the final row's reveal animation land before the sheet slides up.
        setTimeout(() => setOverlayOpen(true), 700);
        track(ANALYTICS_EVENTS.GAME_COMPLETED, { game_key: FOOTBALLER_GAME_KEY, won: res.won, guesses_used: res.guessesUsed });
      }
    } catch (err) {
      flashToast(extractErrorMessage(err, `Couldn't guess ${label}`));
    } finally {
      setBusy(false);
    }
  }, [apiClient, busy, phase, syncCoins, invalidateXp, track, flashToast]);

  /** Tapping a suggestion guesses it directly. */
  const onSelectSuggestion = useCallback((s: FootballerSuggestion) => {
    setInput(s.name);
    selectedId.current = s.id;
    void submit(s.id, s.name);
  }, [submit]);

  /** The arrow button / Enter: submit the chosen (or top-matching) suggestion. */
  const onSubmit = useCallback(() => {
    if (busy || phase !== 'playing') return;
    const typed = input.trim();
    if (!typed) return;
    if (selectedId.current) {
      void submit(selectedId.current, typed);
      return;
    }
    const exact = suggestions.find((s) => s.name.toLowerCase() === typed.toLowerCase());
    const pick = exact ?? (suggestions.length === 1 ? suggestions[0] : null);
    if (!pick) {
      flashToast('Player not found — pick from the list');
      return;
    }
    void submit(pick.id, pick.name);
  }, [busy, phase, input, suggestions, submit, flashToast]);

  const onFocus = useCallback(() => { if (suggestions.length > 0) setShowDropdown(true); }, [suggestions.length]);
  const onBlur = useCallback(() => { setTimeout(() => setShowDropdown(false), 150); }, []);

  return {
    phase,
    isLoading: phase === 'loading',
    isError: phase === 'error',
    errorMessage,
    maxGuesses,
    guesses,
    guessesUsed: guesses.length,
    guessesLeft,
    streak,
    result,
    overlayOpen,
    setOverlayOpen,
    input,
    suggestions,
    showDropdown,
    busy,
    toast,
    onInputChange,
    onSelectSuggestion,
    onSubmit,
    onFocus,
    onBlur,
    retry: load,
    flashToast,
  };
}

export type UseFootballerQuizGame = ReturnType<typeof useFootballerQuizGame>;
